import '../../../components/text-and-lang/as-text.ts';
import '../../../components/parameters/as-param-group.ts';
import '../../../components/inputs/buttons/as-button.ts';
import '../../../components/overlays/as-hovercard-wrapper.ts';
import '../../../components/overlays/as-hovercard.ts';
import { AsFileInput, FILE_INPUT_ATTR_SPECS, warnFileSelectedListenerDeprecated, type AsFileHandle, type RemoveFileCallback } from './as-file-input.ts';
import { internalData } from '../../../store/store.ts';
import { autoAttrs, attrNameOf, prefixAttrs } from '../../../util/autoAttrs.ts';
import { getFileImportProvider } from './fileImportProvider.ts';
import styleText from './as-import-group.css?raw';
import { applyLocalizedText } from '../../../components/base.ts';

// Every as-file-input attribute is mirrored from the group host onto the inner
// upload, so the group can be configured declaratively. Sourced from
// as-file-input's own FILE_INPUT_ATTR_SPECS so the list is never duplicated.
//
// Attributes already shown/used in real markup or the published docs (Outlines2,
// the tutorial's live demo and its "recommended usage" example blocks) stay bare
// for back-compat — renaming an attribute that's already shipped would break
// existing markup. Everything else gets a `file-input-` prefix, so its derived
// property (e.g. `group.fileInputMultiple`) reads as clearly distinct from
// `group.fileInput.multiple` instead of colliding/ambiguous bare names.
const FILE_INPUT_BACKCOMPAT_ATTRS = [
  'category', 'accept', 'intercepts-dropped', 'button-text', 'button-text-dict-key',
  'drop-text', 'drop-text-dict-key',
  'minimal-file-list', 'trcad-param', 'configure-on-change',
  // no-accepts-label has bespoke GrabCAD-env logic below (_syncUploadAttr,
  // connectedCallback) keyed on this exact literal — kept bare so that logic
  // doesn't need touching.
  'no-accepts-label',
] as const;

const PREFIXED_FILE_INPUT_ATTRS = prefixAttrs(FILE_INPUT_ATTR_SPECS, 'file-input', {
  ignore: FILE_INPUT_BACKCOMPAT_ATTRS,
});

// Host attribute name (bare for the back-compat set above, `file-input-`-prefixed
// otherwise) -> the real as-file-input attribute name it forwards to.
// FILE_INPUT_ATTR_SPECS and PREFIXED_FILE_INPUT_ATTRS stay index-aligned
// (prefixAttrs preserves position), so zipping them by index is safe.
const UPLOAD_ATTR_MAP: ReadonlyMap<string, string> = new Map(
  FILE_INPUT_ATTR_SPECS.map((spec, i) => [attrNameOf(PREFIXED_FILE_INPUT_ATTRS[i]), attrNameOf(spec)] as const)
);
const UPLOAD_PASSTHROUGH_ATTRS: readonly string[] = [...UPLOAD_ATTR_MAP.keys()];

// as-hovercard-wrapper config attributes. When a child <as-hovercard> is
// adopted into one of the group's wrappers, these are mirrored from the card
// onto its wrapper, so consumers tune placement etc. on the card they author
// (the wrappers themselves live inside the group's generated markup).
const HOVER_WRAPPER_CONFIG_ATTRS: readonly string[] = [
  'placement', 'offset', 'delay', 'hide-delay', 'show-on-hover', 'toggle-on-click', 'keep-open',
];

/**
 * <as-import-group>
 *
 * Drop-in import card: renders an <as-param-group> in light DOM (so it
 * participates directly in the parent layout, and inner events bubble up
 * through the host unchanged) containing:
 *
 *   - subheading        "Import {subject}" (default: "Import Workpiece")
 *   - hint text         "Recommended format: .STL" (<as-text>)
 *   - <as-file-input>   the drop zone / file picker, in a hovercard wrapper
 *   - <as-button>       "Try sample data", in a hovercard wrapper
 *
 * Attributes
 *   id                      Optional. The inner file input gets `<id>-file-input`
 *                           and the sample button `<id>-sample-button`. When
 *                           absent, an id is derived and reflected back onto the
 *                           host: the trcad-param value if set; otherwise the id
 *                           of the parent element (or the closest <as-step-panel>
 *                           with an id) plus "-import-group", numbered by
 *                           position when that container holds several import
 *                           groups (e.g. "step-1-import-group-2").
 *   subject-default-text    Subject word in the heading (default: "Workpiece")
 *   subject-dict-key        Localization key for the subject word
 *   subheading-default-text Heading template; "{0}" / "{}" is replaced by the
 *                           subject (default: "Import {0}")
 *   subheading-dict-key     Localization key for the heading template — its
 *                           value should contain "{0}" for the subject, which
 *                           is passed via subheading-vars
 *   hint-default-text       Hint line (default: "Recommended format: .STL",
 *                           or a registered import provider's own
 *                           acceptHintText when one is active for the current
 *                           env and no explicit value is set — see
 *                           fileImportProvider.ts)
 *   hint-dict-key           Localization key for the hint line
 *   sample-default-text     Sample button label (default: "Try sample data")
 *   sample-dict-key         Localization key for the sample button label
 *   sample-tooltip-default-text  Tooltip on the sample button (default: "Try
 *                           the app with sample data - no file needed")
 *   sample-tooltip-dict-key Localization key for the sample button tooltip
 *   no-sample-button        Boolean. Hides the "Try sample data" button row
 *
 * Every attribute above (except id and no-sample-button) also has a matching
 * camelCase property accessor (get/set) that reflects the attribute, e.g.
 * `group.subheadingDefaultText`, `group.sampleDictKey`. `noSampleButton` is a
 * boolean property mirroring the attribute's presence.
 *
 * Localization: as long as the host hasn't overridden a piece's *-default-text,
 * it falls back to a baked-in dict key (fileIO.import.subheading/subject/hint/
 * sampleButton/sampleTooltip in sharedLocalisation.json) instead of staying
 * English-only — same as passing that key via *-dict-key yourself. Setting
 * *-default-text without *-dict-key opts that piece out of translation, since
 * the text is now app-specific copy.
 *
 * All <as-file-input> attributes are passed through to the inner upload, so
 * the group is a drop-in replacement for a bare <as-file-input>. Most keep
 * their bare name for back-compat with existing markup and docs — category,
 * accept, intercepts-dropped, button-text, button-text-dict-key, drop-text,
 * drop-text-dict-key, minimal-file-list, trcad-param, configure-on-change,
 * no-accepts-label. The rest (multiple,
 * filetype, compact, label-default-text, auto-upload-file, file-id,
 * delete-on-clear, grabcad-stl-unit) are `file-input-`-prefixed on the group
 * (e.g. `file-input-multiple`, property `group.fileInputMultiple`) so their
 * derived property doesn't read as ambiguous next to `group.fileInput.multiple`
 * — the bare attribute still works directly on `group.fileInput`. In the GrabCAD env
 * (internalData.appEnv === 'grabcad') the "Accepts:" hint is hidden
 * automatically — imports go through GrabCAD's own open/convert dialog there,
 * which also supplies its own hint-default-text (the real list of formats
 * that dialog accepts) in place of the generic ".STL" default.
 *
 * Hovercards
 *   The drop zone and the sample button are each wrapped in an
 *   <as-hovercard-wrapper>. Author an <as-hovercard> as a light-DOM child of
 *   the group and it is adopted into the matching wrapper, selected by its
 *   `for` attribute: "file-input" or "sample-button". Cards without `for` fill
 *   in order — the first goes to the file input, the next to the sample
 *   button. Wrapper config attributes (placement, offset, delay, hide-delay,
 *   show-on-hover, toggle-on-click, keep-open) set on the card are forwarded
 *   to its wrapper.
 *
 * Events (all bubble + composed, dispatched by the inner components through
 * this light-DOM host)
 *   as-button-click         From the sample button (buttonName
 *                           "try-sample-data", id `<id>-sample-button`)
 *   as-files-selected / as-files-change / as-file-removed / change / ...
 *                           Re-broadcast naturally from the inner
 *                           <as-file-input> — see its docs (as-file-selected is
 *                           a deprecated alias of as-files-change).
 *                           For removal there is also a callback form:
 *                           group.onRemoveFile((removedFile, fileList, reason) => ...)
 *                           `reason` ('user' | 'inputs-reset' | 'api') is also on
 *                           the as-file-removed detail — check it before acting,
 *                           a reset is not a user removal.
 *
 * Element access (for configuring the parts directly)
 *   group.fileInput               the inner <as-file-input>
 *   group.files                   convenience: the inner file input's current
 *                                 selection (AsFileHandle[], empty when none)
 *   group.sampleButton            the inner "Try sample data" <as-button>
 *   group.paramGroup              the wrapping <as-param-group>
 *   group.fileHovercardWrapper    the wrapper around the file input
 *   group.sampleHovercardWrapper  the wrapper around the sample button
 */

// Single source of truth for the group's own reflected attribute/property
// pairs (everything in the "Attributes" doc above except id and the
// as-file-input passthrough set). autoAttrs() derives the camelCase property
// name from each attribute name, and this list also feeds observedAttributes
// below — an attribute is only ever named once.
const AUTO_ATTRS = [
  'subheading-default-text',
  'subheading-dict-key',
  'subject-default-text',
  'subject-dict-key',
  'hint-default-text',
  'hint-dict-key',
  'sample-default-text',
  'sample-dict-key',
  'sample-tooltip-default-text',
  'sample-tooltip-dict-key',
  { attr: 'no-sample-button', kind: 'boolean' },
] as const;

export class AsImportGroup extends autoAttrs(HTMLElement, [...AUTO_ATTRS, ...PREFIXED_FILE_INPUT_ATTRS] as const) {
  static readonly tag = 'as-import-group' as const;

  private _built = false;
  private _groupEl: HTMLElement | null = null;
  private _hintEl: HTMLElement | null = null;
  private _hintLinkEl: HTMLAnchorElement | null = null;
  private _fileInputEl: AsFileInput | null = null;
  private _sampleBtnEl: HTMLElement | null = null;
  private _fileHoverEl: HTMLElement | null = null;
  private _sampleHoverEl: HTMLElement | null = null;
  private _removeEnvEffect: (() => void) | null = null;
  // Last id this component derived and reflected itself; while the attribute
  // still equals it, the id stays auto-managed (re-derived when trcad-param or
  // DOM position changes). An author writing any other id takes ownership.
  private _autoId: string | null = null;
  private _syncingId = false;
  private _fallbackId: string | null = null;
  private _hovercardObserver: MutationObserver | null = null;
  // Held until _build creates the inner file input, then forwarded to it.
  private _removeFileCb: RemoveFileCallback | RemoveFileCallback[] | null = null;

  private static _styleInjected = false;
  private static _autoIdSeq = 0;

  static readonly observedAttributes = [
    'id',
    ...AUTO_ATTRS.map(attrNameOf),
    ...UPLOAD_PASSTHROUGH_ATTRS,
  ];

  override addEventListener(type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions): void {
    if (type === 'as-file-selected') warnFileSelectedListenerDeprecated(this.tagName.toLowerCase());
    super.addEventListener(type, listener, options);
  }

  get fileInput(): AsFileInput | null { return this._fileInputEl; }
  /** The inner file input's current selection (empty when none). */
  get files(): readonly AsFileHandle[] { return this._fileInputEl?.files ?? []; }

  /** Upload tokens of the current selection. `files` is the file data. */
  get tokens(): readonly string[] { return this._fileInputEl?.tokens ?? []; }

  /** Register remove-file callback(s) on the inner file input (see
   *  AsFileInput.onRemoveFile / the as-file-removed event). Accepts a single
   *  callback or an array; pass null to unregister. Safe to call before the
   *  group is connected; the callbacks are forwarded once built. */
  onRemoveFile(cb: RemoveFileCallback | RemoveFileCallback[] | null): void {
    this._removeFileCb = cb;
    this._fileInputEl?.onRemoveFile(cb);
  }
  get sampleButton(): HTMLElement | null { return this._sampleBtnEl; }
  get paramGroup(): HTMLElement | null { return this._groupEl; }
  get fileHovercardWrapper(): HTMLElement | null { return this._fileHoverEl; }
  get sampleHovercardWrapper(): HTMLElement | null { return this._sampleHoverEl; }

  connectedCallback(): void {
    if (!AsImportGroup._styleInjected) {
      const style = document.createElement('style');
      style.textContent = styleText;
      style.setAttribute('data-component', 'as-import-group');
      document.head.appendChild(style);
      AsImportGroup._styleInjected = true;
    }
    if (!this._built) this._build();
    this._syncSubheading();
    this._syncHint();
    this._syncSampleLabel();
    this._syncSampleTooltip();
    this._syncId();
    this._adoptHovercards();
    // Hovercard children may be parsed (or appended) after this connect ran;
    // adopt them into their wrapper whenever they show up.
    this._hovercardObserver ??= new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof HTMLElement && node.tagName.toLowerCase() === 'as-hovercard') {
            this._adoptHovercard(node);
          }
        }
      }
    });
    this._hovercardObserver.observe(this, { childList: true });
    // env can be set late (GrabCAD org detection after userData), so re-sync
    // the accepts hint (and the accept-label toggle) when it arrives.
    this._removeEnvEffect = internalData.subscribeKey('appEnv', () => {
      this._syncUploadAttr('no-accepts-label');
      this._syncHint();
    });
    this._syncUploadAttr('no-accepts-label');
  }

  disconnectedCallback(): void {
    this._removeEnvEffect?.();
    this._removeEnvEffect = null;
    this._hovercardObserver?.disconnect();
  }

  attributeChangedCallback(name: string, _old: string | null, _new: string | null): void {
    if (!this._built) return;
    if (name === 'id') {
      this._syncId();
    } else if (UPLOAD_PASSTHROUGH_ATTRS.includes(name)) {
      this._syncUploadAttr(name);
      if (name === 'trcad-param') this._syncId();
    } else if (name.startsWith('hint-')) {
      this._syncHint();
    } else if (name.startsWith('sample-tooltip-')) {
      this._syncSampleTooltip();
    } else if (name.startsWith('sample-')) {
      this._syncSampleLabel();
    } else if (name === 'no-sample-button') {
      // Purely CSS-driven (as-import-group.css); nothing to sync.
    } else {
      this._syncSubheading();
    }
  }

  private _build(): void {
    this._built = true;
    const tpl = document.createElement('template');
    tpl.innerHTML = /*html*/`
      <as-param-group>
        <p class="import-hint-row">
          <as-text class="import-hint"></as-text>
          <a class="import-hint-link" target="_blank" rel="noopener noreferrer" hidden></a>
        </p>
        <as-hovercard-wrapper class="import-file-hover" show-on-hover placement="right">
          <as-file-input></as-file-input>
        </as-hovercard-wrapper>
        <div class="import-sample-row">
          <as-hovercard-wrapper class="import-sample-hover" show-on-hover placement="top">
            <as-button
              name="try-sample-data"
              dict-key="fileIO.import.trySample"
              default-text="Try sample data"
              no-default-icon>
            </as-button>
          </as-hovercard-wrapper>
        </div>
      </as-param-group>
    `;
    const frag = tpl.content;
    this._groupEl       = frag.querySelector('as-param-group') as HTMLElement;
    this._hintEl        = frag.querySelector('.import-hint') as HTMLElement;
    this._hintLinkEl    = frag.querySelector('.import-hint-link') as HTMLAnchorElement;
    this._fileInputEl      = frag.querySelector('as-file-input') as AsFileInput;
    this._sampleBtnEl   = frag.querySelector('as-button') as HTMLElement;
    this._fileHoverEl   = frag.querySelector('.import-file-hover') as HTMLElement;
    this._sampleHoverEl = frag.querySelector('.import-sample-hover') as HTMLElement;

    for (const attr of UPLOAD_PASSTHROUGH_ATTRS) this._syncUploadAttr(attr);
    if (this._removeFileCb) this._fileInputEl?.onRemoveFile(this._removeFileCb);

    this.appendChild(frag);
  }

  /** Move author-provided <as-hovercard> children into their target wrapper. */
  private _adoptHovercards(): void {
    for (const child of Array.from(this.children)) {
      if (child instanceof HTMLElement && child.tagName.toLowerCase() === 'as-hovercard') {
        this._adoptHovercard(child);
      }
    }
  }

  private _adoptHovercard(card: HTMLElement): void {
    const target = card.getAttribute('for');
    let wrapper: HTMLElement | null;
    if (target === 'sample-button' || target === 'try-sample-data' || (target && target === this._sampleBtnEl?.id)) {
      wrapper = this._sampleHoverEl;
    } else if (target) {
      wrapper = this._fileHoverEl;
    } else {
      // No explicit target: fill in order — file input first, sample button
      // next (the common authoring shape is one card for each).
      wrapper = this._fileHoverEl?.querySelector('as-hovercard') ? this._sampleHoverEl : this._fileHoverEl;
    }
    if (!wrapper || card.parentElement === wrapper) return;
    for (const attr of HOVER_WRAPPER_CONFIG_ATTRS) {
      const value = card.getAttribute(attr);
      if (value !== null) wrapper.setAttribute(attr, value);
    }
    wrapper.appendChild(card);
  }

  /**
   * Resolve the group's effective id and stamp `<id>-file-input` /
   * `<id>-sample-button` on the inner elements. A derived id is reflected onto
   * the host attribute so it is visible in the DOM and stable for consumers.
   */
  private _syncId(): void {
    if (!this._built || this._syncingId) return;
    const explicit = this.getAttribute('id');
    let id = explicit;
    if (!id || id === this._autoId) {
      id = this._deriveId();
      this._autoId = id;
      if (explicit !== id) {
        this._syncingId = true;
        try { this.setAttribute('id', id); } finally { this._syncingId = false; }
      }
    } else {
      this._autoId = null;
    }
    if (this._fileInputEl) this._fileInputEl.id = `${id}-file-input`;
    if (this._sampleBtnEl) this._sampleBtnEl.id = `${id}-sample-button`;
  }

  private _deriveId(): string {
    const param = this.getAttribute('trcad-param');
    if (param) return param;

    const parent = this.parentElement;
    const stepPanel = this.closest('as-step-panel');
    const container = parent?.id ? parent : (stepPanel?.id ? stepPanel : null);
    if (container) {
      const groups = Array.from(container.querySelectorAll('as-import-group'));
      const index = groups.indexOf(this);
      const suffix = groups.length > 1 ? `-${index + 1}` : '';
      return `${container.id}-import-group${suffix}`;
    }

    this._fallbackId ??= `import-group-${++AsImportGroup._autoIdSeq}`;
    return this._fallbackId;
  }

  /** `hostName` is the attribute as set on the group (bare for the
   *  back-compat set, `file-input-`-prefixed otherwise) — forwards to the real
   *  as-file-input attribute name via UPLOAD_ATTR_MAP. */
  private _syncUploadAttr(hostName: string): void {
    if (!this._fileInputEl) return;
    const innerName = UPLOAD_ATTR_MAP.get(hostName) ?? hostName;
    let value = this.getAttribute(hostName);
    // In the GrabCAD env imports go through GrabCAD's own open/convert dialog,
    // so the browser-style "Accepts:" hint is noise — hide it there even
    // without the host attribute.
    if (innerName === 'no-accepts-label' && value === null && internalData.appEnv === 'grabcad') value = '';
    if (value === null) this._fileInputEl.removeAttribute(innerName);
    else this._fileInputEl.setAttribute(innerName, value);
  }

  private _syncSubheading(): void {
    if (!this._groupEl) return;
    const authorTemplate = this.subheadingDefaultText;
    const template = authorTemplate ?? 'Import {0}';
    const authorSubject = this.subjectDefaultText;
    const subjectDefault = authorSubject ?? 'Workpiece';
    this._groupEl.setAttribute('subheading-default-text', template.replace(/\{0\}|\{\}/g, subjectDefault));

    // Fall back to the component's own baked-in dict keys when the host hasn't
    // overridden the template/subject text — an author who supplies their own
    // default-text is authoring app-specific copy, so it should never be
    // silently swapped for our generic translation.
    const dictKey = this.subheadingDictKey ?? (authorTemplate == null ? 'fileIO.import.subheading' : null);
    if (dictKey) {
      const subjectDictKey = this.subjectDictKey ?? (authorSubject == null ? 'fileIO.import.subject' : null);
      const subjectVar = subjectDictKey
        ? { dictKey: subjectDictKey, defaultText: subjectDefault }
        : subjectDefault;
      this._groupEl.setAttribute('subheading-vars', JSON.stringify([subjectVar]));
      this._groupEl.setAttribute('subheading-dict-key', dictKey);
    } else {
      this._groupEl.removeAttribute('subheading-dict-key');
      this._groupEl.removeAttribute('subheading-vars');
    }
  }

  private _syncHint(): void {
    if (!this._hintEl) return;
    // A registered import provider (e.g. GrabCAD's own open/convert dialog)
    // replaces the native file input outright, so the generic ".STL" default
    // — written for that native input — no longer describes what's actually
    // accepted. Fall through to the provider's own hint text (and optional
    // link) when the host hasn't set an explicit hint-default-text.
    const explicit = this.hintDefaultText;
    const provider = explicit == null ? getFileImportProvider(internalData.appEnv) : null;
    // Only fall back to our own baked-in key for our own baked-in default text —
    // an explicit host override or a provider's own hint text isn't ours to translate.
    const dictKey = this.hintDictKey ?? (explicit == null && !provider ? 'fileIO.import.hint' : null);
    applyLocalizedText(this._hintEl, dictKey, explicit ?? provider?.acceptHintText ?? 'Recommended format: .STL');

    if (this._hintLinkEl) {
      if (provider?.acceptHintUrl) {
        this._hintLinkEl.href = provider.acceptHintUrl;
        this._hintLinkEl.innerHTML = '';
        if (provider.acceptHintLinkText) {
          this._hintLinkEl.textContent = provider.acceptHintLinkText;
        } else {
          const linkText = document.createElement('as-text');
          applyLocalizedText(linkText, 'fileIO.import.hintLinkDefault', 'Click here');
          this._hintLinkEl.appendChild(linkText);
        }
        this._hintLinkEl.hidden = false;
      } else {
        this._hintLinkEl.hidden = true;
      }
    }
  }

  private _syncSampleLabel(): void {
    if (!this._sampleBtnEl) return;
    const authorText = this.sampleDefaultText;
    const dictKey = this.sampleDictKey ?? (authorText == null ? 'fileIO.import.sampleButton' : null);
    applyLocalizedText(this._sampleBtnEl, dictKey, authorText ?? 'Try sample data');
  }

  private _syncSampleTooltip(): void {
    if (!this._sampleBtnEl) return;
    const authorText = this.sampleTooltipDefaultText;
    this._sampleBtnEl.setAttribute('tooltip-default-text', authorText ?? 'Try the app with sample data - no file needed');
    const dictKey = this.sampleTooltipDictKey ?? (authorText == null ? 'fileIO.import.sampleTooltip' : null);
    if (dictKey) this._sampleBtnEl.setAttribute('tooltip-dict-key', dictKey);
    else this._sampleBtnEl.removeAttribute('tooltip-dict-key');
  }

  // subheadingDefaultText, subheadingDictKey, subjectDefaultText, subjectDictKey,
  // hintDefaultText, hintDictKey, sampleDefaultText, sampleDictKey,
  // sampleTooltipDefaultText, sampleTooltipDictKey, noSampleButton — all
  // defined by autoAttrs(AUTO_ATTRS) above the class.
}

if (!customElements.get(AsImportGroup.tag)) {
  customElements.define(AsImportGroup.tag, AsImportGroup);
} else {
  console.warn('as-import-group already defined');
}
